/**
 * کارنامه‌ی رجیستری — مایگریشن 0085.
 *
 * یک سؤال را جواب می‌دهد: **کدام ردیفِ رجیستری ارزشش را دارد؟** — و جوابش
 * را نه با «چند رویداد داد»، بلکه با چیزی می‌دهد که اگر آن منبع را حذف کنی
 * از دست می‌رود. شرحِ کامل در `plan/prompts/registry-scorecard.md`.
 *
 * سومین فایلِ repo برای اسکیمای `crawler` است و این عمدی است، به همان
 * دلیلی که `sourceRepo` از `opsRepo` جدا شد: `opsRepo` **شمارشِ خطِ لوله**
 * است، `sourceRepo` **ردیفِ رجیستری**، و این‌جا **ارزشِ** هر ردیف. سه سؤال
 * با سه ریتم؛ یکی کردنشان یعنی فایلی که هر تغییری در هر سربرگ به آن دست
 * می‌زند.
 *
 * ═══ ⚠ دو خانواده‌ی عدد، و قاطی کردنشان بدترین اشتباهِ خواندنِ این داده ═══
 *
 *   `load`  چه گذشت — و از مایگریشنِ ۰۱۰۸ هر عددش **جفت** است:
 *           `run` در آخرین اجرا، `d10` در ۱۰ روزِ گذشته. انتخابگرِ لود
 *           حذف شد، پس «این اجرا» همیشه یعنی آخرین اجرا.
 *   `state` بعد از آن اجرا **وضعیت** چه بود — ولی فقط تا جایی که جدولش
 *           تاریخچه دارد. انحصار از `cluster_membership_history` می‌آید که
 *           نسل‌دار است و دقیق بازسازی می‌شود؛ سهمِ فیلدی از
 *           `canonical_events`/`merge_field_decisions` که upsert می‌شوند و
 *           فقط «الان» را دارند.
 *
 * ⚠ **و `state` جفت نشد.** انحصار از عضویتِ یک نسل درمی‌آید و نسل یکجا
 * ساخته می‌شود؛ «انحصارِ ۱۰ روزِ اخیر» یا همان عددِ الان است یا صفر. دو
 * ستونی که همیشه یک عدد را دو بار بنویسند نویزند، نه ستون. رابط بالای
 * همان بلوک می‌نویسد که این‌ها وضعیت **پس از آخرین اجرا**ست.
 */
import { supabase } from '../lib/supabase';
import type { Counts } from './opsRepo';

/** روی دیتابیسی که اسکیمای `crawler` را ندارد، به‌جای خطا این برمی‌گردد. */
export interface RegistryUnavailable { error: string }

/**
 * یک اجرای خطِ لوله.
 *
 * ⚠ `versions` **آرایه** می‌دهد و نه عدد: اگر مرحله‌ای وسطِ لود نسخه عوض
 * کند دو عضو دارد، و `max` گرفتن دقیقاً همان چیزی را پنهان می‌کرد که این
 * ستون برای نشان دادنش هست.
 *
 * ⚠ مرحله‌ای که در این لود **نچرخیده** اصلاً کلید ندارد، نه اینکه نسخه‌ی
 * قبلی را بگیرد. لودی که فقط `policy` را دوانده باید با لودی که کلِ زنجیره
 * را دوانده فرق داشته باشد، وگرنه مقایسه‌ی دو لود بی‌معنی است.
 */
export interface RegistryLoad {
  id: string;
  mode: string | null;
  command: string | null;
  status: string | null;
  flags: Record<string, boolean> | null;
  /**
   * این اجرا کلِ زنجیره را دوانده یا نه. کارنامه همیشه آخرین اجرای **کامل**
   * را می‌گیرد؛ `false` یعنی هیچ اجرای کاملی ثبت نشده (مایگریشنِ ۰۱۰۹).
   */
  full: boolean;
  started_at: string;
  finished_at: string | null;
  seconds: number | null;
  generation_id: string | null;
  versions: Record<string, (number | null)[]>;
}

/**
 * جریان — هر عدد دو بار.
 *
 * ⚠ ستونِ سومِ («روی هم رفته») این بلوک عمداً نیست: «۳۸ هزار درخواست از
 * ابتدای تاریخ» نه به کارِ کسی می‌آید نه ارزشِ یک گذرِ بی‌شرط روی
 * `fetch_events` را دارد.
 */
export interface RegistryLoadStats {
  requests: Counts;
  fetched: Counts;
  unchanged_304: Counts;
  errors: Counts;
  blocked: Counts;
  throttled: Counts;
  /** فقط انتظارِ شبکه. */
  net_ms: Counts;
  /** فقط خوابِ خودمان پشتِ `min_interval_seconds` — عمداً از `net_ms` جداست. */
  wait_ms: Counts;
  bytes: Counts;
  /** سهم از `net_ms`ِ **همان پنجره**؛ مخرجِ هر ستون مالِ خودش است. */
  pct_fetch_time: { d10: number; run: number };
  candidates_new: Counts;
  candidates_seen: Counts;
  claims: Counts;
  claim_fields: Counts;
  mentions: Counts;
  matches: { d10: Record<string, number>; run: Record<string, number> };
  publish: { d10: Record<string, number>; run: Record<string, number> };
  published: Counts;
  /** خوشه‌هایی که hashشان عوض شد، بی‌آنکه لزوماً چیزی عوض شده باشد. */
  churned: Counts;
}

/** شمارشِ میزبانِ لینک‌ها. دو محورِ جدا و هر دو لازم — نگاه کن به prompt. */
export type HostBreakdown = Record<string, number>;

export interface RegistryState {
  /** جمله‌ای که می‌گوید کدام عدد مالِ لودِ انتخابی است و کدام مالِ امروز. */
  as_of: string;
  clusters: number;
  shared: number;
  exclusive: number;
  /**
   * از آن انحصارها، چند تا یک `uncertain` به mentionِ منبعِ دیگری دارند.
   *
   * ⚠ **«انحصاری» با «ارزشمند» یکی نیست.** خوشه‌ی تک‌منبعی یا پوششِ
   * انحصاریِ واقعی است (ارزش) یا شکستِ matcher (توهم). امروز روی داده‌ی
   * واقعی ۲۳۰ از ۳۲۷ انحصارِ `613cal` مشکوک‌اند، پس از ستونِ `exclusive`
   * نمی‌شود تصمیم گرفت.
   */
  exclusive_suspect: number;
  /** تنها عددی که تصمیم می‌سازد. */
  exclusive_publishable: number;
  publishable: number;
  sole_field_events: number;
  sole_fields: number;
  won_fields: number;
  lost_fields: number;
  conflicted_fields: number;
  avg_fields_filled: number;
  required_ok: number;
  past_events: number;
  ref_hosts: HostBreakdown;
  event_hosts: HostBreakdown;
  median_lead_seconds: number | null;
  first_to_find: number;
}

/**
 * `measured: false` یعنی «در این لود به شبکه نزدیم»، نه «مجانی بود».
 * صفر گذاشتن به‌جایش گران‌ترین منبع را در لودی که فقط merge بوده
 * مجانی‌ترین نشان می‌داد.
 */
export interface RegistryCost {
  measured?: boolean;
  ms_per_published?: number | null;
  requests_per_published?: number | null;
  ms_per_sole_field?: number | null;
}

export interface RegistryRow {
  source_id: string;
  label: string | null;
  city: string | null;
  tier: string | null;
  trust: number | null;
  enabled: boolean;
  aggregator: boolean;
  draft_only: boolean;
  extractor: string | null;
  goout_source: string | null;
  /**
   * شناسه‌هایی که در این ردیف جمع شده‌اند. درفت و منبع یک سایت‌اند
   * (`draft:dubai:x` و `x`) و کلیدِ ادغام **قرارداد** است نه ارجاع — پس
   * این ستون تنها جایی است که یک ادغامِ اشتباه دیده می‌شود.
   */
  merged_from: string[];
  hygiene: string[];
  sort_key: number;
  load: RegistryLoadStats;
  state: RegistryState;
  cost: RegistryCost;
}

export interface RegistryScorecard {
  error?: string;
  load?: RegistryLoad;
  rows?: RegistryRow[];
  /**
   * ⚠ از جمعِ ردیف‌ها **درنمی‌آید** و نباید بیاید: خوشه‌ی دومنبعی در ردیفِ
   * هر دو شمرده می‌شود، پس جمعِ ستون‌ها از کلِ رویدادها بیشتر است و این
   * درست است نه باگ.
   */
  totals?: { clusters: number; registries: number };
}

/** یک فیلد در کارنامه‌ی یک منبع. */
export interface RegistryField {
  field: string;
  events: number;
  filled: number;
  /**
   * ⚠ از **خروجی** خوانده می‌شود نه از نبودِ ورودی: فیلدی که claim داشته
   * ولی merge کنارش گذاشته، برای کاربر خالی است.
   */
  empty: number;
  sole_provider: number;
  won: number;
  lost: number;
}

export interface RegistryFields {
  error?: string;
  source_id?: string | null;
  generation_id?: string | null;
  fields?: RegistryField[];
}

/** یک جفت منبع با خوشه‌ی مشترک. */
export interface RegistryPair {
  source_a: string;
  source_b: string;
  shared: number;
  events_a: number;
  events_b: number;
  jaccard: number;
}

export interface RegistryOverlap {
  error?: string;
  generation_id?: string | null;
  pairs?: RegistryPair[];
}

/**
 * چهار حالتِ نمونه.
 *
 * ⚠ **مهم‌ترین تابعِ این مجموعه، با اینکه کوچک‌ترین است.** تاریخچه‌ی این
 * پروژه می‌گوید هیچ تعمیرِ واقعی از عدد درنیامد؛ باگِ چنددسته‌ایِ `opl` و
 * «Extreme»→متال هر دو با چشم پیدا شدند و عددِ جمع هر دو را پنهان کرده بود.
 * عدد می‌گوید کجا را نگاه کن؛ این می‌گوید چه چیزی آن‌جاست.
 */
export type RegistrySampleKind = 'empty' | 'sole' | 'lost' | 'conflict';

export interface RegistrySample {
  cluster_id: string;
  title: string | null;
  /** ⚠ لحظه‌ی UTC. رندر کردنش با منطقه‌ی زمانیِ مرورگر یک بار دو لیبل را خراب کرد. */
  starts_at: string | null;
  url: string | null;
  final_value: string | null;
  our_value: string | null;
  winning_value: string | null;
  winner: string | null;
}

export interface RegistrySamples {
  error?: string;
  kind?: RegistrySampleKind;
  field?: string | null;
  source_id?: string | null;
  generation_id?: string | null;
  samples?: RegistrySample[];
}

const call = async <T>(fn: string, args: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw error;
  return data as T;
};

export const registryRepo = {
  /**
   * فهرستِ اجراها.
   *
   * ⚠ رابط دیگر انتخابگرِ لود ندارد (مایگریشنِ ۰۱۰۸) و این تابع فقط برای
   * نگاهِ دستی مانده. `p_load`ِ بقیه‌ی توابع هم همیشه `null` می‌رود، یعنی
   * «آخرین».
   */
  loads: (limit = 30) => call<RegistryLoad[]>('admin_registry_loads', { p_limit: limit }),

  /**
   * ⚠ فیلترِ شهر ردیف‌های `draft-only` را بیرون می‌اندازد و این عارضه است
   * نه انتخاب: پیش‌نویسی که ترفیع نگرفته در `source_versions` نیست، پس
   * `city` ندارد. برای دیدنشان فیلتر برداشته می‌شود.
   */
  scorecard: (city?: string | null, limit = 200) =>
    call<RegistryScorecard>('admin_registry_scorecard', {
      p_city: city ?? null, p_limit: limit,
    }),

  fields: (load?: string | null, source?: string | null) =>
    call<RegistryFields>('admin_registry_fields', {
      p_load: load ?? null, p_source: source ?? null,
    }),

  /**
   * ⚠ خالی بودنش **یافته است، نه نبودِ داده**: یا منابع واقعاً پوششِ جدا
   * دارند، یا matcher کار نمی‌کند — و ستونِ `exclusive_suspect` می‌گوید کدام.
   */
  overlap: (load?: string | null, minShared = 1) =>
    call<RegistryOverlap>('admin_registry_overlap', {
      p_load: load ?? null, p_min: minShared,
    }),

  samples: (
    source: string | null, field: string | null,
    kind: RegistrySampleKind, limit = 5,
  ) => call<RegistrySamples>('admin_registry_samples', {
    p_source: source, p_field: field, p_kind: kind, p_limit: limit,
  }),
};
